import { useEffect, useRef } from 'react';
import { t } from '../../../i18n/strings.js';

// Depoimentos de EXEMPLO (banco de imagens). Cada card carrega a etiqueta
// visível "Exemplo" + aviso abaixo do carrossel, pra não parecer cliente real
// até a Gladis trocar por provas verdadeiras (basta pôr `isPlaceholder:false`).
export default function SocialProofCarousel({ images = [] }) {
  const trackRef = useRef(null);

  // Rolagem CONTÍNUA e fluida (estilo esteira): começa quando o carrossel
  // aparece na tela e segue sem parar, em loop infinito (a lista é
  // duplicada e a posição volta à metade sem salto visível). Ao tocar/
  // arrastar, pausa e retoma ~1,5 s depois de soltar. Sem animação se o
  // aparelho pede "reduzir movimento".
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const SPEED = 45; // px por segundo
    let pos = el.scrollLeft;
    let last = 0;
    let raf = 0;
    let visible = false;
    let resumeAt = 0;
    let touching = false;

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = last ? Math.min(now - last, 50) : 16;
      last = now;
      if (!visible || touching || now < resumeAt) {
        pos = el.scrollLeft; // acompanha o gesto da pessoa
        return;
      }
      const half = el.scrollWidth / 2;
      pos += (SPEED * dt) / 1000;
      if (half > 0 && pos >= half) pos -= half;
      el.scrollLeft = pos;
    };
    const down = () => { touching = true; };
    const up = () => { touching = false; resumeAt = performance.now() + 1500; };
    el.addEventListener('touchstart', down, { passive: true });
    el.addEventListener('touchend', up, { passive: true });
    el.addEventListener('touchcancel', up, { passive: true });
    el.addEventListener('wheel', up, { passive: true });
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(el);
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      el.removeEventListener('touchstart', down);
      el.removeEventListener('touchend', up);
      el.removeEventListener('touchcancel', up);
      el.removeEventListener('wheel', up);
    };
  }, []);

  const items = [1, 2, 3, 4].map((n) => ({
    image: images[n - 1],
    isPlaceholder: true,
    category: t(`paywallIntl.testimonial${n}_category`),
    title: t(`paywallIntl.testimonial${n}_title`),
    result: t(`paywallIntl.testimonial${n}_result`),
    quote: t(`paywallIntl.testimonial${n}_quote`),
  }));
  return (
    <section>
      <h2 className="font-display font-extrabold text-[28px] leading-tight text-center">{t('paywallIntl.resultsTitle')}</h2>
      <p className="text-[15px] text-ink-muted text-center mt-2">{t('paywallIntl.resultsSubtitle')}</p>
      <div ref={trackRef} className="flex gap-3 mt-4 -mx-5 px-5 overflow-x-auto pb-2">
        {[...items, ...items].map((tm, i) => (
          <article key={i} aria-hidden={i >= items.length || undefined} className="shrink-0 w-[78%] bg-surface rounded-[20px] overflow-hidden border border-white/5">
            {tm.image && (
              <div className="relative w-full" style={{ aspectRatio: '3 / 2' }}>
                <img
                  src={tm.image}
                  alt=""
                  width={900}
                  height={600}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-3.5">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-bold text-cyan uppercase tracking-wide">{tm.category}</div>
              </div>
              <div className="font-display font-bold text-[16px] mt-1">{tm.title}</div>
              <div className="text-[13px] font-semibold text-ink mt-0.5">{tm.result}</div>
              <p className="text-[13px] text-ink-muted mt-2 border-l-2 border-cyan pl-2">{tm.quote}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
